import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { config } from '../config';
import { clearAccountSuspension } from '@/lib/accountSuspension';

export interface SuspensionSummary {
  suspended: boolean;
  /** `active`, `pastdue`, `suspended` or `canceled`. */
  status: string;
  adminSuspended: boolean;
  reason: string;
  suspendedAt: string | null;
  lastPaymentFailedAt: string | null;
  /** Activated (billable) devices whose monitoring the suspension stopped. */
  deviceCount: number;
  gatewayCount: number;
}

/**
 * The lockout screen's own data: why the account is suspended, and the scale of
 * what has stopped. Served by `/api/billing/suspension`, which stays inside the
 * suspended allowlist.
 *
 * Polls, and lifts the lockout as soon as the account is good again. Doing it
 * here rather than in the payment forms means *any* route back to good standing
 * releases the app — a card added on the billing page, a Stripe webhook landing
 * after a retry, or Witina staff clearing an administrative suspension — with
 * no reload and nothing for the user to discover.
 */
export function useSuspensionSummary(enabled = true) {
  const query = useQuery<SuspensionSummary>({
    queryKey: ['suspensionSummary'],
    queryFn: async () => {
      const { data } = await axios.get<SuspensionSummary>(
        `${config.backendApi}/api/billing/suspension`,
        { withCredentials: true },
      );
      return data;
    },
    enabled,
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
    retry: false,
  });

  const stillSuspended = query.data?.suspended;
  useEffect(() => {
    if (stillSuspended === false) {
      clearAccountSuspension();
    }
  }, [stillSuspended]);

  return query;
}
